import { useState } from 'react';
import { Alert, Button, Card, Modal, Form, Spinner } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { http } from '../../features/jobs/api/http';

/**
 * Wipes for resetting between test runs — what used to be a DELETE from Postman
 * before :5100 started asking for a token.
 *
 * Every one of these destroys live factory state and none of it comes back.
 * That is the whole point of the panel, and also why each one asks you to type
 * the word: these sit on the dashboard of an application that is, at any hour,
 * also running eight machines. A misplaced click is the difference between a
 * clean test and a shift's production gone. Typing four letters costs a second
 * and cannot happen by accident.
 *
 * They report counts rather than "done": on a wipe, "it worked" is not
 * information — the numbers are the only way to tell a clean reset from a
 * half-finished one.
 */

const ACTIONS = [
  {
    key: 'keys',
    label: 'Delete Redis Keys',
    word: 'KEYS',
    url: '/redis/keys',
    variant: 'danger',
    warning:
      'Flushes EVERY key in Redis DB1 — jobs, orders, queues, machine state, ' +
      'the lot. This is the whole running factory, not a subset.'
  },
  {
    key: 'orders',
    label: 'Delete Orders',
    word: 'ORDERS',
    url: '/debug/wipe/orders',
    variant: 'outline-danger',
    warning: 'Removes every order:* key. Jobs and their production stay.'
  },
  {
    key: 'activity',
    label: 'Delete Activity',
    word: 'ACTIVITY',
    url: '/debug/wipe/activity',
    variant: 'outline-warning',
    warning:
      'Clears the activity timeline — the live list on the dashboard AND the ' +
      'stored history behind the Activity page. Production itself is untouched; ' +
      'what goes is the record of who did what.'
  },
  {
    key: 'reports',
    label: 'Delete Reports',
    word: 'REPORTS',
    url: '/debug/wipe/reports',
    variant: 'outline-warning',
    warning:
      'Clears the report timeline, live and stored. Executions keep their own ' +
      'numbers — this removes the events written about them.'
  },
  {
    key: 'warehouse',
    label: 'Delete Warehouse Queue',
    word: 'QUEUE',
    url: '/debug/wipe/warehouse-pending',
    variant: 'outline-warning',
    warning:
      'Empties the Production → Warehouse queue: everything sitting in the yard ' +
      'or on the floor waiting to be put away. Drums already placed on a rack ' +
      'are warehouse stock and are NOT touched, nor is the movement history.'
  },
  {
    key: 'warehouse-stock',
    label: 'Empty Warehouse',
    word: 'STOCK',
    url: '/debug/wipe/warehouse-stock',
    variant: 'outline-danger',
    warning:
      'Removes everything the warehouse HOLDS — every stored drum and pallet, ' +
      'its movement history, the pick and repack tasks, the packing pallets and ' +
      'the shipment trips. Racks and buildings are kept: those are the layout of ' +
      'a real building, not test data. Occupied slots are freed.'
  },
  {
    key: 'completed-jobs',
    label: 'Delete Completed Jobs',
    word: 'FINISHED',
    url: '/debug/wipe/completed-jobs',
    variant: 'outline-warning',
    warning:
      'Clears the job archive in MongoDB — what the Completed tab lists, plus ' +
      'deleted projects. A Redis wipe never touched these, which is why a ' +
      '"clean" board could still show a hundred finished jobs. Live jobs stay.'
  },
  {
    key: 'completed-orders',
    label: 'Delete Completed Orders',
    word: 'DELIVERED',
    url: '/debug/wipe/completed-orders',
    variant: 'outline-warning',
    warning:
      'Clears the order archive in MongoDB — delivered orders and deleted ones. ' +
      'Live orders in Redis are untouched.'
  },
  {
    key: 'operator-performance',
    label: 'Delete Operator Performance',
    word: 'PERFORMANCE',
    url: '/debug/wipe/operator-performance',
    variant: 'outline-warning',
    warning:
      'Removes every production metric, execution log, changeover and machine ' +
      'interval. That is the whole record of who ran what and how fast — the ' +
      'operator pages go blank. The products themselves are untouched.'
  },
  {
    key: 'jobs',
    label: 'Delete Jobs',
    word: 'JOBS',
    url: '/debug/wipe/jobs',
    variant: 'outline-danger',
    warning:
      'Removes every job, the machine queues and execution state that point at ' +
      'them, and the operator-performance records they generated in MongoDB. ' +
      'Orders keep their allocations — delete those too for a clean slate.'
  }
];

const TestResetPanel = () => {
  const [pending, setPending] = useState(null); // the action awaiting confirmation
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const close = () => {
    setPending(null);
    setTyped('');
  };

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await http.delete(pending.url);
      setResult({ label: pending.label, data });
      close();
    } catch (err) {
      setError(
        err?.response?.data?.error || err?.message || 'Request failed'
      );
      close();
    } finally {
      setBusy(false);
    }
  };

  const confirmed = typed.trim().toUpperCase() === pending?.word;

  return (
    <>
      <Card className="border-danger border-2 mt-5">
        <Card.Body>
          <div className="d-flex align-items-center gap-2 mb-2">
            <FeatherIcon icon="alert-triangle" size={18} className="text-danger" />
            <h4 className="mb-0 text-danger">Test reset</h4>
          </div>
          <p className="text-700 mb-3">
            Destructive, immediate and not reversible. Meant for clearing the
            board between test runs — not for use while the factory is running.
          </p>

          <div className="d-flex flex-wrap gap-2">
            {ACTIONS.map(action => (
              <Button
                key={action.key}
                variant={action.variant}
                size="sm"
                onClick={() => {
                  setTyped('');
                  setResult(null);
                  setError(null);
                  setPending(action);
                }}
              >
                {action.label.toUpperCase()}
              </Button>
            ))}
          </div>

          {error && (
            <Alert variant="danger" className="py-2 mt-3 mb-0">
              {error}
            </Alert>
          )}

          {result && (
            <Alert variant="success" className="py-2 mt-3 mb-0">
              <div className="fw-semibold mb-1">{result.label} — done</div>
              <pre className="mb-0 fs-9 text-body">
                {JSON.stringify(result.data.deleted ?? result.data, null, 2)}
              </pre>
              {result.data.note && (
                <div className="mt-2 fs-9 text-700">{result.data.note}</div>
              )}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!pending} onHide={close} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center gap-2 text-danger fs-7">
            <FeatherIcon icon="alert-triangle" size={18} />
            {pending?.label}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-700">{pending?.warning}</p>
          <p className="text-700 mb-2">
            Type <code className="text-danger fw-bold">{pending?.word}</code> to
            confirm. There is no undo.
          </p>
          <Form.Control
            autoFocus
            value={typed}
            onChange={e => setTyped(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && confirmed && !busy) run();
            }}
            placeholder={pending?.word}
          />
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button variant="phoenix-secondary" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={run} disabled={!confirmed || busy}>
            {busy ? <Spinner animation="border" size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TestResetPanel;
